/**
 * Unauthenticated deployment-environment fetch (#3312).
 *
 * The same image runs in production and in a test environment on a copy of
 * production data, so the environment is a runtime fact, not a build-time
 * one. Plain `fetch` for the same reason as `publicBranding.ts`: it must work
 * before anyone has signed in.
 */

export type DeploymentEnvironment = 'production' | 'test';

/** Browser error tracking config (#3620); absent when no browser DSN is set. */
export interface BrowserErrorTrackingConfig {
  dsn: string;
  environment: string;
  release: string | null;
}

interface PublicEnvironment {
  environment: DeploymentEnvironment;
  errorTracking: BrowserErrorTrackingConfig | null;
}

const API_BASE_URL = '/api/v1';

let cached: Promise<PublicEnvironment> | null = null;

function parseErrorTracking(value: unknown): BrowserErrorTrackingConfig | null {
  if (value === null || typeof value !== 'object') return null;
  const { dsn, environment, release } = value as Record<string, unknown>;
  if (typeof dsn !== 'string' || dsn === '' || typeof environment !== 'string') return null;
  return { dsn, environment, release: typeof release === 'string' ? release : null };
}

async function loadPublicEnvironment(): Promise<PublicEnvironment> {
  const response = await fetch(`${API_BASE_URL}/public/environment`);
  if (!response.ok) throw new Error('Failed to load environment');
  const data = (await response.json()) as { environment?: unknown; errorTracking?: unknown };
  return {
    environment: data.environment === 'test' ? 'test' : 'production',
    errorTracking: parseErrorTracking(data.errorTracking),
  };
}

/**
 * Cached for the life of the page: the environment does not change under a
 * running tab. A failed fetch is not cached, so the next mount retries.
 */
function fetchPublicConfig(): Promise<PublicEnvironment> {
  if (cached) return cached;
  cached = loadPublicEnvironment().catch((error: unknown) => {
    cached = null;
    throw error;
  });
  return cached;
}

export async function fetchPublicEnvironment(): Promise<DeploymentEnvironment> {
  return (await fetchPublicConfig()).environment;
}

/** Same request as the environment; null when tracking is off (#3620). */
export async function fetchPublicErrorTracking(): Promise<BrowserErrorTrackingConfig | null> {
  return (await fetchPublicConfig()).errorTracking;
}

/** Test seam. */
export function resetPublicEnvironmentCache(): void {
  cached = null;
}
